import { useEffect, useState } from "react";
import { MapPin, Clock, Phone, Mail, ExternalLink } from "lucide-react";
import { Chapter, SectionTitle, Reveal } from "./Reveal";
import { ADDRESS, GOOGLE_MAPS_URL, MAPS_EMBED, PHONE_DISPLAY, PHONE_TEL, gaEvent } from "@/data/content";
import { hasConsent } from "./CookieBanner";

const INFO = [
  {
    icon: MapPin,
    title: "Indirizzo",
    lines: ["Via Martiri della Niccioleta 13", "Isola pedonale — 58022 Follonica (GR)"],
  },
  {
    icon: Clock,
    title: "Orari",
    lines: ["Lunedì — Sabato: 19:30 – 22:30", "Domenica: chiuso"],
  },
  {
    icon: Phone,
    title: "Telefono",
    lines: [PHONE_DISPLAY],
    href: PHONE_TEL,
    ga: "click_telefono",
  },
  {
    icon: Mail,
    title: "Email",
    lines: ["info@johntartarino.it"],
    href: "mailto:info@johntartarino.it",
    ga: "click_email",
  },
];

const Location = () => {
  const [consent, setConsent] = useState(hasConsent());

  useEffect(() => {
    const onAccept = () => setConsent(true);
    window.addEventListener("jt-consent-accepted", onAccept);
    return () => window.removeEventListener("jt-consent-accepted", onAccept);
  }, []);

  const loadMap = () => {
    localStorage.setItem("jt-consent", "yes");
    setConsent(true);
  };

  return (
  <section id="dove-siamo" data-testid="location-section" className="relative py-24 sm:py-36">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Chapter number="08" title="Dove Siamo" />
      <SectionTitle>
        Nel cuore pedonale di Follonica.
      </SectionTitle>

      <div className="mt-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-4" data-testid="contact-details">
          {INFO.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.08}>
              <div className="flex gap-5 border border-[#F7F4EE]/10 bg-[#141210] p-6 hover:border-[#C5A059]/40 transition-colors duration-300">
                <item.icon size={22} strokeWidth={1.5} className="text-[#C5A059] shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD]">{item.title}</h3>
                  {item.lines.map((l) =>
                    item.href ? (
                      <a
                        key={l}
                        href={item.href}
                        onClick={item.ga ? () => gaEvent(item.ga, { posizione: "contatti" }) : undefined}
                        className="block mt-2 text-lg font-display text-[#F7F4EE] hover:text-[#C5A059] transition-colors"
                      >
                        {l}
                      </a>
                    ) : (
                      <p key={l} className="mt-2 text-base text-[#F7F4EE]/90">{l}</p>
                    )
                  )}
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <a
              data-testid="google-maps-link"
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => gaEvent("click_mappa", { posizione: "contatti" })}
              className="inline-flex items-center gap-3 px-7 py-3.5 border border-[#C5A059]/60 text-[#C5A059] text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-[#0D0C0B] transition-colors duration-300"
            >
              Apri in Google Maps
              <ExternalLink size={15} />
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="relative border border-[#C5A059]/20 overflow-hidden h-[380px] lg:h-full min-h-[380px]">
            {consent ? (
              <iframe
                data-testid="google-map-iframe"
                title="Mappa — John Tartarino, Follonica"
                src={MAPS_EMBED}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full grayscale invert-[0.92] hue-rotate-180 contrast-[0.9]"
                allowFullScreen
              />
            ) : (
              <div
                data-testid="map-consent-placeholder"
                className="w-full h-full bg-[#1C1917] flex flex-col items-center justify-center gap-5 p-8 text-center"
              >
                <MapPin size={30} strokeWidth={1.25} className="text-[#C5A059]" />
                <p className="text-sm text-[#C5BCAD] max-w-xs leading-relaxed">
                  La mappa di Google si carica solo con il tuo consenso.
                </p>
                <button
                  data-testid="map-load-button"
                  onClick={loadMap}
                  className="px-7 py-3 border border-[#C5A059]/60 text-[#C5A059] text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-[#0D0C0B] transition-colors duration-300"
                >
                  Carica la mappa
                </button>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

export default Location;
